import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Send, Bot, User, Sparkles, ArrowRight, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { productsAPI } from '../lib/api';
import { Link } from 'react-router-dom';
import {
  DEFAULT_PRODUCT_ICON_PATH,
  getProductIconSrcForProduct,
} from '../lib/productIcon';

const GPT_CATALOG_QUESTIONS = [
  {
    id: 'purpose',
    text: 'Что будет делать программа? Ответ напишите простым языком кратко в 2-3 предложения.',
    key: 'purpose',
  },
  {
    id: 'targetAudience',
    text: 'Для кого нужна программа?',
    key: 'targetAudience',
  },
  {
    id: 'userCount',
    text: 'Сколько будет пользователей? В ответе можно дать приблизительное число пользователей.',
    key: 'userCount',
  },
];

const GPTCatalogPage = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({
    purpose: null,
    targetAudience: null,
    userCount: null,
  });
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatBootstrapRef = useRef(false);

  // Загрузка продуктов при монтировании
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const products = await productsAPI.getProducts();
        setAllProducts(products);
        setLoading(false);
      } catch (error) {
        console.error('Error loading products:', error);
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  // Инициализация первого сообщения и первого вопроса (после загрузки каталога)
  useEffect(() => {
    if (loading) return;
    if (chatBootstrapRef.current) return;
    if (messages.length > 0) return;
    chatBootstrapRef.current = true;
    const welcomeMessage = {
      id: 1,
      role: 'assistant',
      content: 'Здравствуйте! Я помогу вам подобрать подходящие решения из нашего каталога. Давайте начнем с нескольких вопросов.',
    };
    setMessages([welcomeMessage]);
    const first = GPT_CATALOG_QUESTIONS[0];
    const t = setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          role: 'assistant',
          content: first.text,
          isQuestion: true,
        },
      ]);
      setCurrentQuestionIndex(0);
    }, 1500);
    return () => clearTimeout(t);
  }, [loading, messages.length]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [messages]);

  const askQuestion = (index) => {
    if (index >= GPT_CATALOG_QUESTIONS.length) {
      // Все вопросы заданы, анализируем ответы и подбираем продукты
      analyzeAndRecommend();
      return;
    }

    const question = GPT_CATALOG_QUESTIONS[index];
    const questionMessage = {
      id: Date.now(),
      role: 'assistant',
      content: question.text,
      isQuestion: true,
    };

    setMessages((prev) => [...prev, questionMessage]);
    setCurrentQuestionIndex(index);
  };

  const analyzeAndRecommend = () => {
    setIsTyping(true);
    
    setTimeout(() => {
      // Простая логика анализа ответов для подбора продуктов
      const filteredProducts = filterProductsByAnswers(userAnswers, allProducts);
      
      const analysisMessage = {
        id: Date.now(),
        role: 'assistant',
        content: 'Отлично! На основе ваших ответов я подобрал для вас подходящие решения:',
        isAnalysis: true,
      };

      setMessages((prev) => [...prev, analysisMessage]);
      setRecommendedProducts(filteredProducts);
      setIsTyping(false);
    }, 1000);
  };

  const filterProductsByAnswers = (answers, products) => {
    if (!products || products.length === 0) return [];

    const { purpose, targetAudience, userCount } = answers;
    const purposeLower = (purpose || '').toLowerCase();
    const audienceLower = (targetAudience || '').toLowerCase();
    
    // Извлекаем число пользователей
    const userCountNum = extractUserCount(userCount);

    // Ключевые слова для категорий
    const keywords = {
      crm: ['crm', 'срм', 'продаж', 'сделк', 'менеджер', 'клиент'],
      telephony: ['звон', 'телефон', 'атс', 'звонок', 'звонки', 'телефония'],
      ai: ['ai', 'ии', 'искусствен', 'нейро', 'бот', 'агент', 'автоматизац'],
      support: ['поддержк', 'оператор', 'клиент', 'обработк', 'запрос'],
      analytics: ['аналитик', 'отчет', 'данн', 'метрик', 'kpi'],
      integration: ['интеграц', 'подключен', 'синхронизац', 'обмен'],
    };

    // Подсчитываем релевантность каждого продукта
    const scoredProducts = products.map((product) => {
      let score = 0;
      const productText = `${product.name} ${product.short_description || ''} ${(product.features || []).join(' ')}`.toLowerCase();
      const productCategory = product.category || '';

      // Проверка по назначению программы
      if (purposeLower) {
        Object.entries(keywords).forEach(([category, words]) => {
          words.forEach((word) => {
            if (purposeLower.includes(word) && (productCategory === category || productText.includes(word))) {
              score += 2;
            }
          });
        });
      }

      // Проверка по целевой аудитории
      if (audienceLower) {
        if (audienceLower.includes('менеджер') || audienceLower.includes('продаж')) {
          if (productCategory === 'crm' || productCategory === 'communications') {
            score += 3;
          }
        }
        if (audienceLower.includes('поддержк') || audienceLower.includes('оператор')) {
          if (productCategory === 'support' || productCategory === 'ai') {
            score += 3;
          }
        }
        if (audienceLower.includes('компани') || audienceLower.includes('бизнес')) {
          score += 1;
        }
      }

      // Проверка по количеству пользователей
      if (userCountNum > 0) {
        if (userCountNum < 10 && productCategory === 'communications') {
          score += 1;
        } else if (userCountNum >= 10 && userCountNum < 50) {
          score += 1;
        } else if (userCountNum >= 50) {
          if (productCategory === 'integration' || productCategory === 'analytics') {
            score += 2;
          }
        }
      }

      // Бонус за совпадение ситуаций
      if (product.situations && product.situations.length > 0) {
        score += 1;
      }

      return { ...product, score };
    });

    // Сортируем по релевантности и берем топ-6
    const topProducts = scoredProducts
      .sort((a, b) => b.score - a.score)
      .filter((p) => p.score > 0)
      .slice(0, 6);

    // Если не нашли подходящих, возвращаем популярные продукты
    if (topProducts.length === 0) {
      return products.slice(0, 6);
    }

    return topProducts;
  };

  const extractUserCount = (text) => {
    if (!text) return 0;
    const numbers = text.match(/\d+/g);
    if (numbers && numbers.length > 0) {
      return parseInt(numbers[0], 10);
    }
    
    // Проверка текстовых описаний
    const textLower = text.toLowerCase();
    if (textLower.includes('несколько') || textLower.includes('мало')) return 5;
    if (textLower.includes('много') || textLower.includes('сотн')) return 100;
    if (textLower.includes('десятк')) return 20;
    
    return 0;
  };

  const handleSend = async () => {
    if (!input.trim() || isTyping) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: input.trim(),
    };

    setMessages((prev) => [...prev, userMessage]);
    
    // Сохраняем ответ пользователя
    const currentQuestion = GPT_CATALOG_QUESTIONS[currentQuestionIndex];
    if (currentQuestion) {
      setUserAnswers((prev) => ({
        ...prev,
        [currentQuestion.key]: input.trim(),
      }));
    }

    setInput('');
    setIsTyping(true);

    // Симуляция задержки ответа
    setTimeout(() => {
      setIsTyping(false);
      
      // Если это был ответ на последний вопрос, переходим к анализу
      if (currentQuestionIndex === GPT_CATALOG_QUESTIONS.length - 1) {
        askQuestion(currentQuestionIndex + 1);
      } else {
        // Задаем следующий вопрос
        askQuestion(currentQuestionIndex + 1);
      }
    }, 800);
  };

  const handleRestart = () => {
    chatBootstrapRef.current = false;
    setMessages([]);
    setInput('');
    setCurrentQuestionIndex(0);
    setUserAnswers({
      purpose: null,
      targetAudience: null,
      userCount: null,
    });
    setRecommendedProducts([]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead title="GPT-каталог | Антология" noindex />
      <Header />
      <main className="flex-1 pt-16 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-[calc(100vh-8rem)] flex flex-col">
          {/* Chat Container */}
          <Card className="flex-1 flex flex-col bg-card/95 backdrop-blur-sm border-border/50 shadow-2xl">
            {/* Header */}
            <div className="flex items-center gap-3 p-6 border-b border-border/50">
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Подбор решений</h1>
                <p className="text-sm text-muted-foreground">Ответьте на вопросы, и я подберу подходящие решения</p>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-6">
              <div className="space-y-6 max-w-4xl mx-auto">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex gap-4 ${
                      message.role === 'user' ? 'flex-row-reverse' : ''
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        message.role === 'user' ? 'bg-accent/10' : 'bg-muted'
                      }`}
                    >
                      {message.role === 'user' ? (
                        <User className="w-5 h-5 text-accent" />
                      ) : (
                        <Bot className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                    <div
                      className={`flex-1 ${
                        message.role === 'user'
                          ? 'bg-accent text-accent-foreground rounded-2xl rounded-tr-md px-5 py-3'
                          : 'bg-muted rounded-2xl rounded-tl-md px-5 py-3'
                      }`}
                    >
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                    </div>
                  </div>
                ))}

                {/* Рекомендованные продукты */}
                {recommendedProducts.length > 0 && (
                  <div className="mt-8 space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        <Bot className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1">
                        <div className="grid md:grid-cols-2 gap-3 mt-2">
                          {recommendedProducts.map((product, idx) => {
                            const iconSrc = getProductIconSrcForProduct(product);
                            return (
                              <Card
                                key={product.id}
                                className="bg-card border-border/50 hover:border-accent/50 transition-all hover:shadow-md group"
                                style={{ animationDelay: `${idx * 100}ms` }}
                              >
                                <CardContent className="p-4">
                                  <div className="flex items-start gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 group-hover:bg-accent/20 transition-colors overflow-hidden">
                                      <img
                                        src={iconSrc}
                                        alt=""
                                        className="w-7 h-7 object-contain"
                                        onError={(e) => {
                                          e.currentTarget.onerror = null;
                                          e.currentTarget.src = DEFAULT_PRODUCT_ICON_PATH;
                                        }}
                                      />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-start justify-between gap-2 mb-1">
                                        <h3 className="font-semibold text-sm leading-tight">{product.name}</h3>
                                        <Badge variant="secondary" className="text-xs flex-shrink-0">
                                          {product.category === 'communications' && 'Коммуникации'}
                                          {product.category === 'omnichannel' && 'Омниканальность'}
                                          {product.category === 'ai' && 'AI'}
                                          {product.category === 'analytics' && 'Аналитика'}
                                          {product.category === 'integration' && 'Интеграции'}
                                        </Badge>
                                      </div>
                                      <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
                                        {product.short_description || product.shortDescription}
                                      </p>
                                    </div>
                                  </div>
                                  {product.features && product.features.length > 0 && (
                                    <div className="mb-3 space-y-1">
                                      {product.features.slice(0, 2).map((feature, idx) => (
                                        <div key={idx} className="flex items-start gap-1.5">
                                          <Check className="w-3 h-3 text-accent mt-0.5 flex-shrink-0" />
                                          <span className="text-xs text-muted-foreground line-clamp-1">{feature}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  <div className="flex items-center justify-between pt-3 border-t border-border/30">
                                    <span className="text-sm font-semibold text-accent">{product.price}</span>
                                    <Link to={`/product/${product.id}`}>
                                      <Button variant="ghost" size="sm" className="h-7 text-xs group-hover:text-accent">
                                        Подробнее
                                        <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                                      </Button>
                                    </Link>
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isTyping && (
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                      <Bot className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="bg-muted px-5 py-3 rounded-2xl rounded-tl-md">
                      <div className="typing-indicator flex gap-1">
                        <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="p-6 border-t border-border/50">
              <div className="max-w-4xl mx-auto">
                <div className="flex gap-3">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={
                      currentQuestionIndex < GPT_CATALOG_QUESTIONS.length
                        ? "Введите ваш ответ..."
                        : "Задайте вопрос..."
                    }
                    disabled={isTyping || currentQuestionIndex >= GPT_CATALOG_QUESTIONS.length}
                    className="flex-1 bg-muted/50 border-border/50 focus:border-accent h-12"
                  />
                  {currentQuestionIndex >= GPT_CATALOG_QUESTIONS.length && recommendedProducts.length > 0 && (
                    <Button
                      onClick={handleRestart}
                      variant="outline"
                      className="h-12 px-6"
                    >
                      Начать заново
                    </Button>
                  )}
                  <Button
                    onClick={handleSend}
                    disabled={!input.trim() || isTyping || currentQuestionIndex >= GPT_CATALOG_QUESTIONS.length}
                    className="bg-accent hover:bg-accent/90 text-accent-foreground h-12 px-6"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                {currentQuestionIndex < GPT_CATALOG_QUESTIONS.length && (
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Вопрос {currentQuestionIndex + 1} из {GPT_CATALOG_QUESTIONS.length}
                  </p>
                )}
              </div>
            </div>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default GPTCatalogPage;
